import ApplicationController from "./application_controller"

/*
 * Accessibility — large print (WCAG 1.4.4 Resize Text):
 * Root rem scaling via --lq-text-scale (lexiqamus.scss). Toolbar A−/A/A+ in
 * _accessibility_toolbar.html.erb may be disabled in ERB; restoreTextScale still
 * reapplies a saved multiplier from localStorage on public LexiQamus layouts.
 */
const LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_LOCAL_STORAGE_KEY = "lexiqamusTextScale"
const LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS = [0.875, 1, 1.125, 1.25, 1.5]
const LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER = 1

/*
 * Accessibility — screen magnifier (pointer magnifying lens):
 * Floating toolbar toggle clones #lexiqamus-main into a circular lens that follows
 * the pointer. Lens and mirror use pointer-events: none so clicks/scroll hit the
 * real page (_accessibility_toolbar.html.erb + lexiqamus.scss).
 */
const MAGNIFIER_SCALE = 2
const MAGNIFIER_LENS_SIZE_PX = 180
const MAGNIFIER_CURSOR_OFFSET_PX = 24
const MAGNIFIER_SOURCE_SELECTOR = "#lexiqamus-main"

// Snap stored/custom values to a toolbar step so UI state and CSS stay in sync.
function nearestAllowedTextScale(value) {
  const parsedTextScale = parseFloat(value)
  if (!Number.isFinite(parsedTextScale)) return LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER
  return LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS.find(
    (multiplier) => Math.abs(multiplier - parsedTextScale) < 0.001
  ) || LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER
}

// Map cursor position (coords local to #lexiqamus-main) to clone offset inside the lens.
function magnifierClonePosition(localX, localY, scale, lensSizePx) {
  const radius = lensSizePx / 2
  return {
    left: radius - localX * scale,
    top: radius - localY * scale,
  }
}

// Body data-controller on lexiqamus_public_page + lexiqamus_results layouts.
export default class extends ApplicationController {
  static targets = [
    "decreaseTextButton",
    "resetTextButton",
    "increaseTextButton",
    "magnifierToggle",
    "magnifierLens",
    "magnifierMirror",
  ]

  connect() {
    this.textScale = LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER
    this.magnifierActive = false
    this.lastClientX = 0
    this.lastClientY = 0
    this.magnifierSourceElement = null
    this.restoreTextScale()
  }

  disconnect() {
    this.disableMagnifier()
  }

  // Reapply saved scale on load without firing resize (layout is not dependent on scale yet).
  restoreTextScale() {
    let scale = LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER
    try {
      scale = nearestAllowedTextScale(window.localStorage.getItem(LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_LOCAL_STORAGE_KEY))
    } catch (_e) {
      // ignore localStorage errors (private browsing, quota exceeded)
    }
    this.setLargePrintTextScale(scale, scale !== LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER)
  }

  // Step down one entry in LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS (no-op at minimum).
  decreaseTextSize(event) {
    const currentMultiplierIndex = LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS.indexOf(this.textScale)
    if (currentMultiplierIndex > 0) {
      this.setLargePrintTextScale(LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS[currentMultiplierIndex - 1])
      this.announceFromControl(event?.currentTarget)
    }
  }

  // Step up one entry; indexOf guard covers a scale value that is not an exact step after restore.
  increaseTextSize(event) {
    const currentMultiplierIndex = LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS.indexOf(this.textScale)
    const lastMultiplierIndex = LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS.length - 1
    if (currentMultiplierIndex >= 0 && currentMultiplierIndex < lastMultiplierIndex) {
      this.setLargePrintTextScale(LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS[currentMultiplierIndex + 1])
      this.announceFromControl(event?.currentTarget)
    }
  }

  resetTextSize(event) {
    this.setLargePrintTextScale(LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER)
    this.announceFromControl(event?.currentTarget)
  }

  // Single write path: CSS var drives rem scaling; persist choice; sync toolbar disabled/pressed state.
  setLargePrintTextScale(scale, notifyLayout = true) {
    this.textScale = nearestAllowedTextScale(scale)
    document.documentElement.style.setProperty("--lq-text-scale", String(this.textScale))
    this.updateTextSizeToolbarButtonStates()

    // Best-effort persistence — scaling still works if storage is unavailable.
    try {
      window.localStorage.setItem(LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_LOCAL_STORAGE_KEY, String(this.textScale))
    } catch (_e) {
      // ignore localStorage errors (private browsing, quota exceeded)
    }
    // TOC / scroll-spy and other width listeners recalc after rem scale changes.
    if (notifyLayout) window.dispatchEvent(new Event("resize"))
  }

  /*
   * Accessibility — magnifier toggle (toolbar 🔍):
   * aria-pressed on the button; on/off copy goes to #lexiqamus-status-announcer.
   * Does not announce “on” if lens targets or clone build failed.
   */
  toggleMagnifier(event) {
    if (this.magnifierActive) {
      this.disableMagnifier()
      this.announceMagnifierToggle(event?.currentTarget, false)
    } else if (this.enableMagnifier(event)) {
      this.announceMagnifierToggle(event?.currentTarget, true)
    }
  }

  // Show lens, bind passive document listeners, sync toggle state; never preventDefault on pointer events.
  enableMagnifier(event) {
    if (!this.hasMagnifierLensTarget || !this.hasMagnifierMirrorTarget) return false
    if (!this.buildMagnifierMirror()) return false

    this.lastClientX = event?.clientX ?? window.innerWidth / 2
    this.lastClientY = event?.clientY ?? window.innerHeight / 2
    this.magnifierActive = true

    document.body.classList.add("lexiqamus-magnifier-active")
    this.magnifierLensTarget.hidden = false
    if (this.hasMagnifierToggleTarget) {
      this.magnifierToggleTarget.setAttribute("aria-pressed", "true")
    }

    this.boundMagnifierMove = this.onMagnifierMove.bind(this)
    this.boundMagnifierScroll = this.onMagnifierScroll.bind(this)
    this.boundMagnifierKeydown = this.onMagnifierKeydown.bind(this)
    this.boundMagnifierResize = this.onMagnifierResize.bind(this)

    document.addEventListener("mousemove", this.boundMagnifierMove, { passive: true })
    document.addEventListener("scroll", this.boundMagnifierScroll, { passive: true, capture: true })
    document.addEventListener("keydown", this.boundMagnifierKeydown)
    window.addEventListener("resize", this.boundMagnifierResize, { passive: true })
    this.positionMagnifierAt(this.lastClientX, this.lastClientY)
    return true
  }

  // Tear down listeners and clone; safe to call from disconnect when magnifier was active.
  disableMagnifier() {
    if (!this.magnifierActive) return

    this.magnifierActive = false
    document.body.classList.remove("lexiqamus-magnifier-active")

    if (this.hasMagnifierLensTarget) this.magnifierLensTarget.hidden = true
    if (this.hasMagnifierMirrorTarget) this.magnifierMirrorTarget.innerHTML = ""
    this.magnifierSourceElement = null
    if (this.hasMagnifierToggleTarget) {
      this.magnifierToggleTarget.setAttribute("aria-pressed", "false")
    }

    document.removeEventListener("mousemove", this.boundMagnifierMove)
    document.removeEventListener("scroll", this.boundMagnifierScroll, { capture: true })
    document.removeEventListener("keydown", this.boundMagnifierKeydown)
    window.removeEventListener("resize", this.boundMagnifierResize)
  }

  /*
   * One-time deep clone of main content for the lens mirror. Strip duplicate ids
   * so the clone does not collide with live DOM / skip-link targets. ponytail:
   * mirror can lag AJAX until user toggles off/on; resize rebuilds the clone.
   */
  buildMagnifierMirror() {
    const source = document.querySelector(MAGNIFIER_SOURCE_SELECTOR)
    if (!source || !this.hasMagnifierMirrorTarget) return false

    const clone = source.cloneNode(true)
    this.stripIdsFromSubtree(clone)
    clone.classList.add("lexiqamus-magnifier-clone")
    clone.setAttribute("aria-hidden", "true")
    clone.style.width = `${source.offsetWidth}px`
    clone.style.minHeight = `${source.offsetHeight}px`
    clone.style.transform = `scale(${MAGNIFIER_SCALE})`
    clone.style.transformOrigin = "0 0"

    this.magnifierSourceElement = source
    this.magnifierMirrorTarget.innerHTML = ""
    this.magnifierMirrorTarget.appendChild(clone)
    return true
  }

  stripIdsFromSubtree(root) {
    if (root.id) root.removeAttribute("id")
    root.querySelectorAll("[id]").forEach((node) => node.removeAttribute("id"))
  }

  onMagnifierMove(event) {
    this.lastClientX = event.clientX
    this.lastClientY = event.clientY
    this.positionMagnifierAt(this.lastClientX, this.lastClientY)
  }

  // Recalc clone offset when the page scrolls but the pointer stays still.
  onMagnifierScroll() {
    this.positionMagnifierAt(this.lastClientX, this.lastClientY)
  }

  onMagnifierResize() {
    this.buildMagnifierMirror()
    this.positionMagnifierAt(this.lastClientX, this.lastClientY)
  }

  // WCAG 2.1.1 — Escape exits magnifier without trapping focus in the lens (decorative, aria-hidden).
  onMagnifierKeydown(event) {
    if (event.key !== "Escape") return
    this.disableMagnifier()
    if (this.hasMagnifierToggleTarget) {
      this.announceMagnifierToggle(this.magnifierToggleTarget, false)
    }
  }

  // Fixed lens follows viewport coords; clone uses getBoundingClientRect-relative local coords.
  positionMagnifierAt(clientX, clientY) {
    if (!this.magnifierActive || !this.hasMagnifierLensTarget || !this.hasMagnifierMirrorTarget) return

    const source = this.magnifierSourceElement
    if (!source) return

    const sourceRect = source.getBoundingClientRect()
    const localX = clientX - sourceRect.left
    const localY = clientY - sourceRect.top
    const radius = MAGNIFIER_LENS_SIZE_PX / 2

    this.magnifierLensTarget.style.left = `${clientX - radius + MAGNIFIER_CURSOR_OFFSET_PX}px`
    this.magnifierLensTarget.style.top = `${clientY - radius + MAGNIFIER_CURSOR_OFFSET_PX}px`

    const clone = this.magnifierMirrorTarget.querySelector(".lexiqamus-magnifier-clone")
    if (!clone) return

    const { left, top } = magnifierClonePosition(localX, localY, MAGNIFIER_SCALE, MAGNIFIER_LENS_SIZE_PX)
    clone.style.left = `${left}px`
    clone.style.top = `${top}px`
  }

  // WCAG 4.1.3 — magnifier on/off from data-lexiqamus-accessibility-announce-enabled/disabled on the toggle.
  announceMagnifierToggle(control, enabled) {
    const message = enabled
      ? control?.dataset?.lexiqamusAccessibilityAnnounceEnabled
      : control?.dataset?.lexiqamusAccessibilityAnnounceDisabled
    window.announceLexiqamusStatus?.(message)
  }

  // WCAG 4.1.3 — copy from data-lexiqamus-accessibility-announce on the clicked control (toolbar, sign-in, etc.).
  announceFromControl(control) {
    window.announceLexiqamusStatus?.(control?.dataset?.lexiqamusAccessibilityAnnounce)
  }

  // Disable A− / A+ at the ends of the scale so keyboard users do not activate no-op clicks.
  updateTextSizeToolbarButtonStates() {
    if (this.hasDecreaseTextButtonTarget) {
      this.decreaseTextButtonTarget.disabled = this.textScale <= LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS[0]
    }
    if (this.hasIncreaseTextButtonTarget) {
      const largestTextScaleMultiplier = LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS[
        LEXIQAMUS_ACCESSIBILITY_TEXT_SCALE_MULTIPLIERS.length - 1
      ]
      this.increaseTextButtonTarget.disabled = this.textScale >= largestTextScaleMultiplier
    }
    if (this.hasResetTextButtonTarget) {
      // Reset is “selected” only at the default step (toggle-style control, not a separate mode).
      this.resetTextButtonTarget.setAttribute(
        "aria-pressed",
        this.textScale === LEXIQAMUS_ACCESSIBILITY_DEFAULT_TEXT_SCALE_MULTIPLIER ? "true" : "false"
      )
    }
  }
}

/* Accessibility: screen reader live region in LexiQamus layouts (#lexiqamus-status-announcer).
 * Announces transient status messages (e.g. copied text, section toggles, text scale, magnifier). */
window.announceLexiqamusStatus = function(message) {
  if (!message) return
  const announcer = document.getElementById("lexiqamus-status-announcer")
  if (!announcer) return
  announcer.textContent = ""
  requestAnimationFrame(() => { announcer.textContent = message })
}

// Runtime guard for magnifierClonePosition (fails pack load if math drifts).
;(() => {
  const pos = magnifierClonePosition(100, 50, 2, 180)
  if (pos.left !== -110 || pos.top !== -10) {
    throw new Error("lexiqamus magnifier position self-check failed")
  }
})()
